import i18n from '@/i18n';
/**
 * 方案三：统一图片资产模型。
 * 用显式 role 消掉「ID 指向原图、URL 却该是结果图」的歧义。
 *
 * ImageAsset {
 *   assetId
 *   role: 'original' | 'print' | 'composite' | 'detail'
 *   url                 // 该角色最终可用 URL
 *   derivedFromAssetId  // 血缘
 *   sourceTaskId / sourceModule
 *   legacyImageId       // 兼容旧 imageGroups / printUrl 映射键
 * }
 *
 * 主图 / 详情图约定（已不再用图案裁剪顶主图/详情图）：
 * - composite：主图创作成果 → 导出「主图」
 * - detail：POD 商品设计成果 → 导出「详情图」
 * - 若无 composite，可用第一张 detail 顶上为主图
 */

import {
  isGoodsDerivedPrintSpaceId,
  parseOriginalImageIdFromCropSpaceId,
  resolveGoodsPrintUrl,
} from './goodsPrintUrlResolve';
import {
  inlineUrlFromGoodsImageRef,
  normalizeGoodsImageRefToId,
} from './goodsTemplateImageUrls';

export const IMAGE_ASSET_ROLES = Object.freeze({
  ORIGINAL: 'original',
  PRINT: 'print',
  COMPOSITE: 'composite',
  DETAIL: 'detail',
});

export const IMAGE_ASSET_ROLE_SET = new Set(Object.values(IMAGE_ASSET_ROLES));

/** 主图创作印花位只接受 print */
export const GOODS_STAMP_ACCEPTED_ROLES = Object.freeze([IMAGE_ASSET_ROLES.PRINT]);

/** 模板导出「印花图」只读 print */
export const EXPORT_PRINT_ACCEPTED_ROLES = Object.freeze([IMAGE_ASSET_ROLES.PRINT]);

/** 商品库主图：主图创作(composite)优先，其次 POD 详情图(detail) */
export const LISTING_CAROUSEL_ACCEPTED_ROLES = Object.freeze([
  IMAGE_ASSET_ROLES.COMPOSITE,
  IMAGE_ASSET_ROLES.DETAIL,
]);

/** 原图默认不进印花/主图槽 */
export const LINEAGE_ONLY_ROLES = Object.freeze([IMAGE_ASSET_ROLES.ORIGINAL]);

/**
 * @param {string} role
 * @returns {boolean}
 */
export function isImageAssetRole(role) {
  return IMAGE_ASSET_ROLE_SET.has(String(role || '').trim());
}

/**
 * @param {string} role
 * @param {string[]} allowedRoles
 * @returns {boolean}
 */
export function isRoleAccepted(role, allowedRoles = []) {
  const r = String(role || '').trim();
  return (Array.isArray(allowedRoles) ? allowedRoles : []).includes(r);
}

/**
 * @param {string} role
 * @param {string[]} allowedRoles
 * @param {string} [slotLabel]
 */
export function assertRoleAccepted(role, allowedRoles, slotLabel = i18n.t('workbench:ipeUtilError.slotFallback')) {
  if (isRoleAccepted(role, allowedRoles)) return;
  throw new Error(i18n.t('workbench:ipeUtilError.slotRoleRejected', { slot: slotLabel, roles: allowedRoles.join('/'), role: role || i18n.t('workbench:ipeUtilError.emptyRole') }));
}

function makeAssetId(role, seed = '') {
  const r = String(role || 'asset').trim() || 'asset';
  const s = String(seed || '').trim().replace(/[^\w.-]+/g, '_').slice(0, 48);
  const rand = Math.random().toString(36).slice(2, 8);
  return `ia_${r}_${s || 'x'}_${rand}`;
}

/**
 * @param {object} input
 * @returns {object|null}
 */
export function createImageAsset(input = {}) {
  const role = String(input.role || '').trim();
  const url = String(input.url || '').trim();
  if (!isImageAssetRole(role) || !url) return null;

  const legacyImageId = String(input.legacyImageId || input.imageId || '').trim();
  const derivedFromAssetId = String(input.derivedFromAssetId || '').trim();
  const sourceTaskId = String(input.sourceTaskId || '').trim();
  const sourceModule = String(input.sourceModule || '').trim();
  const name = String(input.name || '').trim();
  const assetId = String(input.assetId || '').trim()
    || makeAssetId(role, legacyImageId || url.slice(-24));

  return {
    assetId,
    role,
    url,
    derivedFromAssetId: derivedFromAssetId || undefined,
    sourceTaskId: sourceTaskId || undefined,
    sourceModule: sourceModule || undefined,
    legacyImageId: legacyImageId || undefined,
    name: name || undefined,
  };
}

/**
 * @param {unknown} raw
 * @returns {object|null}
 */
export function normalizeImageAsset(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return createImageAsset(raw);
}

/**
 * @param {unknown} list
 * @returns {object[]}
 */
export function normalizeImageAssetList(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  list.forEach((item) => {
    const asset = normalizeImageAsset(item);
    if (!asset) return;
    const key = `${asset.role}|${asset.legacyImageId || ''}|${asset.url}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(asset);
  });
  return out;
}

/**
 * @param {object[]} assets
 * @param {string|string[]} roles
 * @returns {object[]}
 */
export function filterImageAssetsByRole(assets, roles) {
  const wanted = new Set(
    (Array.isArray(roles) ? roles : [roles])
      .map((r) => String(r || '').trim())
      .filter(Boolean),
  );
  return normalizeImageAssetList(assets).filter((a) => wanted.has(a.role));
}

/**
 * @param {object[]} assets
 * @returns {string[]}
 */
export function collectUrlsFromImageAssets(assets) {
  return normalizeImageAssetList(assets)
    .map((a) => a.url)
    .filter(Boolean);
}

/** 导出印花：只读 print */
export function collectPrintUrlsFromImageAssets(assets) {
  return collectUrlsFromImageAssets(filterImageAssetsByRole(assets, EXPORT_PRINT_ACCEPTED_ROLES));
}

/** 商品库详情图列：只读 detail（POD 商品设计成果） */
export function collectDetailUrlsFromImageAssets(assets) {
  return collectUrlsFromImageAssets(
    filterImageAssetsByRole(assets, [IMAGE_ASSET_ROLES.DETAIL]),
  );
}

/** 商品库主图候选：composite（主图创作）在前；无主图时 detail（POD）可顶上；展示层仅取首张 */
export function collectCarouselUrlsFromImageAssets(assets) {
  const list = normalizeImageAssetList(assets);
  const composites = collectUrlsFromImageAssets(
    list.filter((a) => a.role === IMAGE_ASSET_ROLES.COMPOSITE),
  );
  const details = collectUrlsFromImageAssets(
    list.filter((a) => a.role === IMAGE_ASSET_ROLES.DETAIL),
  );
  // 有主图创作时候选为全部 composite（首张作主图，其余并入详情图列）
  if (composites.length > 0) return composites;
  return details;
}

/** 原图血缘：只读 original，默认不进印花/主图槽 */
export function collectOriginalUrlsFromImageAssets(assets) {
  return collectUrlsFromImageAssets(filterImageAssetsByRole(assets, LINEAGE_ONLY_ROLES));
}

/**
 * 合并多批资产（同 role+legacyImageId+url 去重；后者不覆盖已有 URL）。
 * @param {...object[]} lists
 * @returns {object[]}
 */
export function mergeImageAssetLists(...lists) {
  const out = [];
  const indexByKey = new Map();
  lists.flat().forEach((raw) => {
    const asset = normalizeImageAsset(raw);
    if (!asset) return;
    const key = `${asset.role}|${asset.legacyImageId || ''}|${asset.url}`;
    if (indexByKey.has(key)) return;
    const softKey = `${asset.role}|${asset.legacyImageId || asset.assetId}`;
    if (asset.legacyImageId && indexByKey.has(softKey)) {
      // 同角色同 legacy id 已有条目则保留先写入的 URL
      return;
    }
    indexByKey.set(key, out.length);
    if (asset.legacyImageId) indexByKey.set(softKey, out.length);
    out.push(asset);
  });
  return out;
}

/**
 * 从 print 资产重建 goodsPrintUrlByImageId（兼容旧读路径）。
 * @param {object[]} assets
 * @returns {Record<string, string>}
 */
export function buildGoodsPrintUrlMapFromAssets(assets) {
  const map = {};
  filterImageAssetsByRole(assets, IMAGE_ASSET_ROLES.PRINT).forEach((asset) => {
    if (asset.legacyImageId) map[String(asset.legacyImageId)] = asset.url;
    map[asset.assetId] = asset.url;
  });
  return map;
}

/**
 * 按 legacyImageId / assetId 取指定角色 URL。
 * @param {object[]} assets
 * @param {string} imageId
 * @param {string|string[]} roles
 * @returns {string}
 */
export function resolveImageAssetUrl(assets, imageId, roles = IMAGE_ASSET_ROLES.PRINT) {
  const id = String(imageId || '').trim();
  if (!id) return '';
  const list = filterImageAssetsByRole(assets, roles);
  const hit = list.find((a) => (
    a.legacyImageId === id
    || a.assetId === id
    || String(a.legacyImageId || '').endsWith(`_${id}`)
    || id.endsWith(`_${a.legacyImageId || ''}`)
  ));
  return hit?.url || '';
}

function inferSourceModuleFromSpaceId(spaceId) {
  const id = String(spaceId || '');
  if (/^crop_/i.test(id)) return 'crop';
  if (/^cutout_/i.test(id)) return 'cutout';
  if (/^enhance_/i.test(id)) return 'enhance';
  return 'goods';
}

function resolveOriginalLegacyId(printLegacyId) {
  const id = String(printLegacyId || '').trim();
  if (!id) return '';
  if (/^crop_/i.test(id)) return parseOriginalImageIdFromCropSpaceId(id);
  const cutout = id.match(/^cutout_[^_]+_(.+)$/i);
  if (cutout?.[1]) return cutout[1];
  const enhance = id.match(/^enhance_[^_]+_(.+)$/i);
  if (enhance?.[1]) return enhance[1];
  return '';
}

/**
 * 从印花引用构建 print（+ 可选 original 血缘）资产。
 * 规则：主图创作印花位只产生 role=print；原图仅在有独立 URL 时写入，且默认不进印花槽。
 * @param {Array<{ id?: string, url?: string, apiUrl?: string, previewUrl?: string, name?: string, originalUrl?: string }>} stampRefs
 * @param {{ sourceTaskId?: string, sourceModule?: string }} [options]
 * @returns {object[]}
 */
export function buildImageAssetsFromStampRefs(stampRefs = [], options = {}) {
  const sourceTaskId = String(options.sourceTaskId || '').trim();
  const sourceModuleHint = String(options.sourceModule || '').trim();
  const assets = [];

  (Array.isArray(stampRefs) ? stampRefs : []).forEach((ref, index) => {
    const legacyImageId = String(ref?.id || '').trim();
    const url = String(ref?.apiUrl || ref?.url || ref?.previewUrl || '').trim();
    if (!legacyImageId || !url) return;

    const sourceModule = sourceModuleHint || inferSourceModuleFromSpaceId(legacyImageId);
    const printAsset = createImageAsset({
      role: IMAGE_ASSET_ROLES.PRINT,
      url,
      legacyImageId,
      name: ref?.name || `印花${index + 1}`,
      sourceTaskId,
      sourceModule,
    });
    if (!printAsset) return;

    const originalLegacyId = resolveOriginalLegacyId(legacyImageId);
    const originalUrl = String(ref?.originalUrl || '').trim();
    if (originalLegacyId && originalUrl && originalUrl !== url) {
      const originalAsset = createImageAsset({
        role: IMAGE_ASSET_ROLES.ORIGINAL,
        url: originalUrl,
        legacyImageId: originalLegacyId,
        sourceTaskId,
        sourceModule: 'upload',
        name: `原图_${originalLegacyId}`,
      });
      if (originalAsset) {
        printAsset.derivedFromAssetId = originalAsset.assetId;
        assets.push(originalAsset);
      }
    } else if (originalLegacyId && originalLegacyId !== legacyImageId) {
      // 仅记血缘 id，不把 print URL 误标成 original
      printAsset.derivedFromAssetId = `ia_original_${originalLegacyId}`;
    }

    assets.push(printAsset);
  });

  return normalizeImageAssetList(assets);
}

/**
 * 从印花 URL 映射 + 可选原图 URL 映射构建资产。
 * @param {Record<string, string>} printUrlByImageId
 * @param {{
 *   originalUrlByImageId?: Record<string, string>,
 *   sourceTaskId?: string,
 *   sourceModule?: string,
 * }} [options]
 */
export function buildImageAssetsFromPrintUrlMap(printUrlByImageId = {}, options = {}) {
  const printMap = printUrlByImageId && typeof printUrlByImageId === 'object' ? printUrlByImageId : {};
  const originalMap = options.originalUrlByImageId && typeof options.originalUrlByImageId === 'object'
    ? options.originalUrlByImageId
    : {};
  const sourceTaskId = String(options.sourceTaskId || '').trim();
  const sourceModule = String(options.sourceModule || '').trim();
  const assets = [];
  const keys = Object.keys(printMap);

  keys.forEach((rawId) => {
    const legacyImageId = String(rawId || '').trim();
    const url = String(printMap[rawId] || '').trim();
    if (!legacyImageId || !url) return;

    // 已有 crop_/cutout_ 结果位时，跳过裸原图别名，避免 print 资产挂错 id
    if (!isGoodsDerivedPrintSpaceId(legacyImageId)) {
      const hasDerivedAlias = keys.some((k) => {
        if (!isGoodsDerivedPrintSpaceId(k)) return false;
        return resolveOriginalLegacyId(k) === legacyImageId
          && String(printMap[k] || '').trim() === url;
      });
      if (hasDerivedAlias) return;
    }

    const printAsset = createImageAsset({
      role: IMAGE_ASSET_ROLES.PRINT,
      url,
      legacyImageId,
      sourceTaskId,
      sourceModule: sourceModule || inferSourceModuleFromSpaceId(legacyImageId),
    });
    if (!printAsset) return;

    const originalLegacyId = resolveOriginalLegacyId(legacyImageId);
    const originalUrl = originalLegacyId
      ? String(originalMap[originalLegacyId] || originalMap[String(originalLegacyId)] || '').trim()
      : '';
    if (originalLegacyId && originalUrl && originalUrl !== url) {
      const originalAsset = createImageAsset({
        role: IMAGE_ASSET_ROLES.ORIGINAL,
        url: originalUrl,
        legacyImageId: originalLegacyId,
        sourceTaskId,
        sourceModule: 'upload',
      });
      if (originalAsset) {
        printAsset.derivedFromAssetId = originalAsset.assetId;
        assets.push(originalAsset);
      }
    } else if (originalLegacyId && originalLegacyId !== legacyImageId) {
      printAsset.derivedFromAssetId = `ia_original_${originalLegacyId}`;
    }
    assets.push(printAsset);
  });

  return normalizeImageAssetList(assets);
}

/**
 * 从 goodsForms.imageGroups 收集 print 资产（内联 url）。
 * @param {object[]} goodsForms
 * @param {{ sourceTaskId?: string, sourceModule?: string }} [options]
 */
export function buildImageAssetsFromGoodsForms(goodsForms = [], options = {}) {
  const sourceTaskId = String(options.sourceTaskId || '').trim();
  const sourceModule = String(options.sourceModule || 'goods').trim();
  const refs = [];
  (Array.isArray(goodsForms) ? goodsForms : []).forEach((form) => {
    (form?.imageGroups || []).forEach((group) => {
      Object.values(group || {}).forEach((ref) => {
        const id = normalizeGoodsImageRefToId(ref);
        const url = inlineUrlFromGoodsImageRef(ref);
        if (!id || !url) return;
        const role = ref && typeof ref === 'object' ? String(ref.role || '').trim() : '';
        // 印花位只收 print；显式 original/composite/detail 不进 print 资产
        if (role && role !== IMAGE_ASSET_ROLES.PRINT) return;
        refs.push({
          id,
          url,
          name: typeof ref === 'object' ? ref.name : '',
          assetId: typeof ref === 'object' ? ref.assetId : '',
        });
      });
    });
  });
  return buildImageAssetsFromStampRefs(refs, { sourceTaskId, sourceModule });
}

/**
 * 从合成结果追加 composite/detail 资产。
 * - 主图创作 → composite（主图）
 * - POD 商品设计 → detail（详情图）
 * @param {object[]} existingAssets
 * @param {object[]} goodsForms
 * @param {{ sourceTaskId?: string, sourceModule?: string }} [options]
 */
export function appendCompositeAssetsFromGoodsForms(existingAssets = [], goodsForms = [], options = {}) {
  const sourceTaskId = String(options.sourceTaskId || '').trim();
  const sourceModule = String(options.sourceModule || 'goods').trim();
  const isPodDesign = sourceModule === 'podProductDesign';
  const added = [];

  (Array.isArray(goodsForms) ? goodsForms : []).forEach((form, formIndex) => {
    (form?.compositeResults || []).forEach((result) => {
      const url = String(result?.compositeUrl || result?.url || '').trim();
      if (!url) return;
      const kind = String(result?.kind || '').trim().toLowerCase();
      let role = IMAGE_ASSET_ROLES.COMPOSITE;
      if (kind === 'detail') role = IMAGE_ASSET_ROLES.DETAIL;
      else if (kind === 'main') role = IMAGE_ASSET_ROLES.COMPOSITE;
      else if (isPodDesign) role = IMAGE_ASSET_ROLES.DETAIL;
      const legacyImageId = String(
        result?.compositeImageId
        || result?.imageId
        || `composite-${formIndex}-${result?.groupIndex ?? 0}-${result?.imageId || ''}`,
      ).trim();
      added.push(createImageAsset({
        role,
        url,
        legacyImageId,
        sourceTaskId,
        sourceModule,
        name: role === IMAGE_ASSET_ROLES.DETAIL ? '详情图' : '主图',
      }));
    });
  });

  return mergeImageAssetLists(existingAssets, added);
}

/**
 * 任务 result 侧统一构建 / 合并 imageAssets（双写兼容旧 printUrl 映射）。
 */
export function buildTaskImageAssetsPayload({
  stampRefs = null,
  printUrlByImageId = null,
  originalUrlByImageId = null,
  goodsForms = null,
  existingAssets = null,
  sourceTaskId = '',
  sourceModule = 'goods',
  includeComposites = false,
} = {}) {
  const parts = [];
  if (Array.isArray(existingAssets)) parts.push(existingAssets);
  if (Array.isArray(stampRefs) && stampRefs.length > 0) {
    parts.push(buildImageAssetsFromStampRefs(stampRefs, { sourceTaskId, sourceModule }));
  }
  if (printUrlByImageId && typeof printUrlByImageId === 'object') {
    parts.push(buildImageAssetsFromPrintUrlMap(printUrlByImageId, {
      originalUrlByImageId,
      sourceTaskId,
      sourceModule,
    }));
  }
  if (Array.isArray(goodsForms) && goodsForms.length > 0) {
    parts.push(buildImageAssetsFromGoodsForms(goodsForms, { sourceTaskId, sourceModule }));
  }
  let assets = mergeImageAssetLists(...parts);
  if (includeComposites && Array.isArray(goodsForms)) {
    assets = appendCompositeAssetsFromGoodsForms(assets, goodsForms, { sourceTaskId, sourceModule });
  }
  return assets;
}

/**
 * 解析印花 URL：优先 imageAssets(role=print)，再回退旧 printUrl 映射。
 */
export function resolvePrintUrlFromAssetsOrMap(imageId, {
  imageAssets = [],
  printUrlByImageId = {},
} = {}) {
  const fromAssets = resolveImageAssetUrl(imageAssets, imageId, IMAGE_ASSET_ROLES.PRINT);
  if (fromAssets) return fromAssets;
  return resolveGoodsPrintUrl(imageId, printUrlByImageId);
}

/**
 * 将任意引用规范为主图创作「印花位」可接受的槽位引用。
 * 规则：只产出 role=print；拒绝 original。
 *
 * @param {object|string|number} ref
 * @param {{ assetId?: string, name?: string, sourceTaskId?: string, sourceModule?: string }} [meta]
 * @returns {{ id: string, url: string, role: 'print', assetId: string, name?: string }}
 */
export function toPrintSlotRef(ref, meta = {}) {
  const legacyImageId = typeof ref === 'object' && ref
    ? String(ref.id || ref.imageId || ref.legacyImageId || '').trim()
    : String(ref ?? '').trim();
  const url = typeof ref === 'object' && ref
    ? String(ref.url || ref.apiUrl || ref.previewUrl || ref.dataUrl || ref.src || '').trim()
    : '';
  const incomingRole = typeof ref === 'object' && ref ? String(ref.role || '').trim() : '';

  if (incomingRole && incomingRole !== IMAGE_ASSET_ROLES.PRINT) {
    assertRoleAccepted(incomingRole, GOODS_STAMP_ACCEPTED_ROLES, '主图创作印花位');
  }
  if (!legacyImageId || !url) {
    throw new Error(i18n.t('workbench:ipeUtilError.printRefMissingIdUrl'));
  }

  const asset = createImageAsset({
    assetId: meta.assetId || (typeof ref === 'object' ? ref.assetId : '') || '',
    role: IMAGE_ASSET_ROLES.PRINT,
    url,
    legacyImageId,
    name: meta.name || (typeof ref === 'object' ? ref.name : '') || '',
    sourceTaskId: meta.sourceTaskId || (typeof ref === 'object' ? ref.sourceTaskId : '') || '',
    sourceModule: meta.sourceModule || (typeof ref === 'object' ? ref.sourceModule : '') || '',
    derivedFromAssetId: typeof ref === 'object' ? ref.derivedFromAssetId : '',
  });
  if (!asset) {
    throw new Error(i18n.t('workbench:ipeUtilError.cannotBuildPrintAsset'));
  }

  return {
    id: asset.legacyImageId,
    url: asset.url,
    role: IMAGE_ASSET_ROLES.PRINT,
    assetId: asset.assetId,
    name: asset.name,
    derivedFromAssetId: asset.derivedFromAssetId,
  };
}

/**
 * 旧任务 result 无 imageAssets 时，从 goodsPrintUrlByImageId / goodsForms / 合成结果迁移。
 * @param {object} taskOrResult
 * @returns {object[]}
 */
export function hydrateImageAssetsFromLegacyResult(taskOrResult) {
  if (!taskOrResult || typeof taskOrResult !== 'object') return [];
  const result = taskOrResult.result && typeof taskOrResult.result === 'object'
    ? taskOrResult.result
    : taskOrResult;
  const existing = normalizeImageAssetList(result.imageAssets || taskOrResult.imageAssets);
  if (existing.length > 0) return existing;

  const printMap = result.goodsPrintUrlByImageId
    && typeof result.goodsPrintUrlByImageId === 'object'
    ? result.goodsPrintUrlByImageId
    : {};
  const goodsForms = Array.isArray(result.goodsForms)
    ? result.goodsForms
    : (Array.isArray(taskOrResult.goodsForms) ? taskOrResult.goodsForms : []);
  const sourceTaskId = String(
    result.sourceCropTaskId
    || result.sourceCutoutTaskId
    || taskOrResult.id
    || '',
  ).trim();
  const sourceModule = String(
    result.billingNavId || result.sourceNav || taskOrResult.billingNavId || 'goods',
  ).trim();

  return buildTaskImageAssetsPayload({
    printUrlByImageId: printMap,
    goodsForms,
    sourceTaskId,
    sourceModule,
    includeComposites: true,
  });
}

/**
 * 读取任务可用的 imageAssets（已有则用，否则从旧字段迁移）。
 * @param {object} task
 * @returns {object[]}
 */
export function resolveTaskImageAssets(task) {
  return hydrateImageAssetsFromLegacyResult(task);
}

/**
 * 从任务资产取印花 URL 映射（只含 print）。
 * @param {object} task
 * @returns {Record<string, string>}
 */
export function resolveTaskPrintUrlMapFromAssets(task) {
  return buildGoodsPrintUrlMapFromAssets(resolveTaskImageAssets(task));
}
